import { Injectable, inject } from '@angular/core';
import { UtilsService } from '../utils/utils.service';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { AudioService } from '../media/audio.service';
import { WaypointService } from './waypoint.service';
import { SettingsService, Unit } from '../account/settings.service';
import { ConsoleLoggerService } from '../logging/console-logger.service';
import { WebviewService } from '../platform/webview.service';
import { VehicleService } from '../vehicle/vehicle.service';
import { GPSLocation } from '../../models/gps-location';
import { NextTurn, Route, RoutePlanElement, RoutePlanType, RoutePoint, Turn } from '../../models/route';
import { StackedModalsService } from '../modal/stacked-modals.service';
import { ChargeState } from '../../models/charge-state';
import { BetterRouteState } from '../../models/better-route';
import { RangeParams } from '../../models/range-params';
import { EvCalculationService } from './ev-calculation.service';
import { CursorService } from '../map/cursor.service';
const INVALID_TARGET = -1;
const REPLAN_TARGET_IDX = -2;
const NumberMaxValue = 9007199254740991;
// fake gps generation consts
const DGTEARTHRADIUS_M: number = 6366707;
const DGTEARTHCIRCUMFERENCE_M: number = 2 * DGTEARTHRADIUS_M * Math.PI;

type ReplayGpsPoint = { mPosition: number[], mDateTime: Date | null, mHeight: number | null, mBearing: number, mSpeed: number | null };

/** Distance from the route line (in the scaled-degree units of LineDist) beyond which the position counts as off the route. */
const REPLANNING_TOLERANCE = 0.0003;

/** First announcement of a turn: below `announceBelow` m it is announced and `schema` is chosen, below `nearBelow` m the near command counts as said. */
interface FirstCommandBand { schema: number, announceBelow: number, nearBelow: number }

/** Follow-up announcements of a schema: the near / far window starts (m) and the distance each one says. */
interface CommandWindows { nearFrom: number, nearSays: number, farFrom: number, farSays: number }

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private TargetIdx: number = INVALID_TARGET;
  private NextChargingStationIdx: number = INVALID_TARGET;
  private TargetWithCommandIdx: number = INVALID_TARGET;
  private LastTargetWithCommandIdx: number = INVALID_TARGET;
  private ReplanningNow: boolean = false;
  private LastCommandMinDist: number = NumberMaxValue;
  private LastCommandDistToShow: number = 0;
  private readonly CommandNowDistance: number = 45;
  private Last5Speed: [number, number, number, number, number] = [50, 50, 50, 50, 50];
  private ChargeState: ChargeState = ChargeState.None;

  private NavigationRoute: Route | null = null;
  private MinDist: number = NumberMaxValue;
  public CurrentPoint: GPSLocation | null = null;
  private avg5Speed: number = 51;
  private NextTurnInstruction: Turn | null = null;
  public slidersValueChangedIn15seconds: boolean = false;

  public ObservableChangeNavigation = new BehaviorSubject<string | null>(null);
  public ObservableNextTurn = new BehaviorSubject<NextTurn | null>(null);
  public ObservableShowRoadLane = new BehaviorSubject<boolean | null>(null);
  public ObservableSetCenter = new BehaviorSubject<boolean | null>(null);
  public ObservableMapDragIn5Seconds = new BehaviorSubject<boolean | null>(null);
  public ObservableNextWaypointIdx = new BehaviorSubject<number | null>(null);
  public ObservableCannotSkipMoreChargingStation = new BehaviorSubject<boolean | null>(null);
  /** A skip was asked for while the next charging stop is the destination itself. */
  public ObservableCannotSkipDestinationCharger = new Subject<void>();
  public ObservableFloatingInfoVisible = new BehaviorSubject<boolean | null>(null);
  public ObservableUserTriggeredReplan = new BehaviorSubject<boolean | null>(null);
  public ObservableOpenNavigationRestartDialog = new BehaviorSubject<boolean | null>(null);
  public ObservableArrivedToCharger = new BehaviorSubject<RoutePlanElement | null>(null);
  public ObservableChargeStateChanged = new BehaviorSubject<ChargeState>(this.ChargeState);
  public ObservableBetterRoute = new BehaviorSubject<{ state: BetterRouteState, minutesDifference: number, routeData: any, rangeParams: RangeParams } | null>(null);
  private NextWaypointIdx: number = 0;
  /** Epoch ms of the last replan request. NavigationLifecycleHandler sets it when navigation starts. */
  public LastReplanTime!: number;
  private floatingInfoInterval!: ReturnType<typeof setTimeout>;

  private ClosingInOnTarget: boolean = false;
  public selectedReplayTrack: Document | null = null;
  private readonly subscriptions: Subscription[] = [];
  public navigation: boolean = false;
  public skipChargingStation: boolean = false;
  private readonly keepAliveTimeStamp: number = Date.now();

  private commandNowSaid: boolean = false;
  private command300mSaid: boolean = false;
  private command800mSaid: boolean = false;
  private commandDistanceSchema: number = 0;
  private lastCommandDist: number = NumberMaxValue;

  private readonly utilsService = inject(UtilsService);
  private readonly audioService = inject(AudioService);
  private readonly waypointService = inject(WaypointService);
  private readonly settingsService = inject(SettingsService);
  private readonly consoleLoggerService = inject(ConsoleLoggerService);
  public readonly webviewService = inject(WebviewService);
  private readonly stackedModalsService = inject(StackedModalsService);
  private readonly evCalculationService = inject(EvCalculationService);
  private readonly vehicleService = inject(VehicleService);
  private readonly cursorService = inject(CursorService);

  constructor() {
    this.subscriptions.push(this.ObservableChangeNavigation.subscribe((resp) => {
      if (resp != undefined && resp == "start") {
        this.navigation = true;
        clearTimeout(this.floatingInfoInterval);
        this.floatingInfoInterval = setTimeout(() => {
          this.ObservableFloatingInfoVisible.next(false);
        }, 10000);
        localStorage.setItem("navigation", "true");
      }
      if (resp != undefined && resp == "exit") {
        this.setBattery(true);
        this.navigation = false;
        localStorage.setItem("navigation", "false");
      }
      if (resp != undefined && resp == "arrive") {
        this.navigation = false;
        localStorage.setItem("navigation", "false");
      }
    }));

    this.checkOngoingNavigation();
  }

  checkOngoingNavigation() {
    if (localStorage.getItem("navigation") == "true") {
      this.ObservableChangeNavigation.next("start");
      this.ObservableOpenNavigationRestartDialog.next(true);
    }
  }

  setSlidersValueChangedIn15seconds(slidersValueChangedIn15seconds: boolean): void {
    this.slidersValueChangedIn15seconds = slidersValueChangedIn15seconds;
  }

  private DegreeDiff(gpsLocation: GPSLocation, idx: number): number {
    const bearing = this.Bearing(gpsLocation.Latitude, gpsLocation.Longitude, this.NavigationRoute!.RoutePoints[idx].Location.lat, this.NavigationRoute!.RoutePoints[idx].Location.lng);
    const course = gpsLocation.Course;
    const angle = Math.abs(bearing - course);
    const normAngle = this.NormAngle(angle);
    return normAngle;
  }

  /*private RefreshNavigation(gpsData) {
    if (this.Points.length > 0) {
      this.Last5Speed += gpsData.Location.Speed;

      this.RefreshTarget(gpsData);

      if (this.TargetIdx >= 0 && this.TargetIdx < this.Points.length && this.NextTurnInstruction.RoutePointIdx != NumberMaxValue) {
        const val, unit;
        this.ConvertHorizontalDistanceString(this.NextTurnDistance(gpsData), val, unit);
      }
    }
  }*/

  private ClearSaidCommands(): void {
    this.commandNowSaid = false;
    this.command300mSaid = false;
    this.command800mSaid = false;
    this.commandDistanceSchema = 0;
    this.lastCommandDist = NumberMaxValue;
  }

  /** Says the next turn at `distance` (0 means "now") and shows its lanes. */
  private SayTurnCommand(distance: number): void {
    this.audioService.readCommand(distance, this.NextTurnInstruction!, this.settingsService.getUnit());
    this.ObservableShowRoadLane.next(true);
  }

  /** Thresholds of the first announcement of a turn for the current speed band, in the speed unit's round numbers. */
  private GetFirstCommandBand(): FirstCommandBand {
    const metric = this.settingsService.getUnit().Speed == Unit.Metric;
    if (this.avg5Speed < 60) {
      return metric
        ? { schema: 300, announceBelow: 300 * 3 / 2, nearBelow: 200 }
        : { schema: 300, announceBelow: this.utilsService.yardToM(300 * 3 / 2), nearBelow: this.utilsService.yardToM(200) };
    }
    if (this.avg5Speed < 100) {
      return metric
        ? { schema: 800, announceBelow: 800 * 3 / 2, nearBelow: 500 }
        : { schema: 800, announceBelow: this.utilsService.yardToM(800 * 3 / 2), nearBelow: this.utilsService.yardToM(500) };
    }
    return metric
      ? { schema: 3000, announceBelow: 3000 * 3 / 2, nearBelow: 1500 }
      : { schema: 3000, announceBelow: this.utilsService.mileToM(2 * 3 / 2), nearBelow: this.utilsService.mileToM(0.65 * 3 / 2) };
  }

  // CURR COMMAND DIST
  private ReadNextCommand(currCommandDist: number): void {
    const band = this.GetFirstCommandBand();
    if (currCommandDist < band.announceBelow) {
      this.commandDistanceSchema = band.schema;
      this.command800mSaid = true;
      if (currCommandDist < band.nearBelow) {
        this.command300mSaid = true;
      }
      if (currCommandDist < this.CommandNowDistance * this.SpeedMul()) {
        this.commandNowSaid = true;
        this.SayTurnCommand(0);
      }
      else {
        this.SayTurnCommand(currCommandDist);
      }
    }
    else {
      this.audioService.readFollowTheRoute(currCommandDist, this.settingsService.getUnit());
    }
  }

  private HandleArrive(): void {
    this.audioService.readCommand(0, this.NextTurnInstruction!, this.settingsService.getUnit());
    this.ObservableChangeNavigation.next("arrive");
    this.ObservableShowRoadLane.next(true);
    this.commandNowSaid = true;
    this.setBattery(true);
    this.waypointService.triggerParamsUpdate();

    this.TargetIdx = INVALID_TARGET;
    this.NextChargingStationIdx = INVALID_TARGET;
    this.TargetWithCommandIdx = INVALID_TARGET;
    this.LastTargetWithCommandIdx = INVALID_TARGET;
    this.NavigationRoute = null;
    this.NextTurnInstruction = null;

    this.ClosingInOnTarget = false;
  }

  private CheckArrive(currCommandDist: number): boolean {
    if ((this.NextTurnInstruction && this.TargetIdx > INVALID_TARGET && this.NextTurnInstruction.Id == "ARRIVE") || this.TargetIdx >= this.NavigationRoute!.RoutePoints.length) {
      if ((this.ClosingInOnTarget && this.lastCommandDist < currCommandDist) || currCommandDist < 5) {
        this.HandleArrive();
        return true;
      }
      if (!this.ClosingInOnTarget && currCommandDist < 30) {
        this.ClosingInOnTarget = true;
      }
    }
    else {
      this.ClosingInOnTarget = false;
    }
    return false;
  }

  private ReadCommands(currCommandDist: number): void {
    if (this.lastCommandDist > currCommandDist) {
      this.ReadApproachingCommand(currCommandDist);
    }
    this.lastCommandDist = currCommandDist;
  }

  /** Follow-up announcements while getting closer to the turn. */
  private ReadApproachingCommand(currCommandDist: number): void {
    if (currCommandDist < this.CommandNowDistance * this.SpeedMul() && !this.commandNowSaid) {
      if (this.NextTurnInstruction && this.NextTurnInstruction.Id != "ARRIVE") {
        this.SayTurnCommand(0);
        this.commandNowSaid = true;
      }
      return;
    }

    const metric = this.settingsService.getUnit().Distance == "metric";
    if (this.commandDistanceSchema == 0) {
      this.commandDistanceSchema = this.PickCommandDistanceSchema(currCommandDist, metric);
      return;
    }

    const windows = this.GetCommandWindows(metric);
    if (windows) {
      this.ReadCommandInWindow(currCommandDist, windows);
    }
  }

  /** The schema for the current speed once the turn is within its range, 0 while still farther. */
  private PickCommandDistanceSchema(currCommandDist: number, metric: boolean): number {
    let schema: number;
    let range: number;
    if (this.avg5Speed < 60) {
      schema = 300;
      range = metric ? 300 * 3 / 2 : this.utilsService.yardToM(300 * 3 / 2);
    }
    else if (this.avg5Speed < 100) {
      schema = 800;
      range = metric ? 800 * 3 / 2 : this.utilsService.mileToM(0.5 * 3 / 2);
    }
    else {
      schema = 3000;
      range = metric ? 3000 * 3 / 2 : this.utilsService.mileToM(2 * 3 / 2);
    }
    return currCommandDist < range ? schema : 0;
  }

  /** The near and far announcement windows of the current schema, in the distance unit's round numbers. */
  private GetCommandWindows(metric: boolean): CommandWindows | null {
    switch (this.commandDistanceSchema) {
      case 300:
        return metric
          ? { nearFrom: 100, nearSays: 100, farFrom: 300, farSays: 300 }
          : { nearFrom: this.utilsService.yardToM(100), nearSays: 100, farFrom: this.utilsService.yardToM(300), farSays: 300 };
      case 800:
        return metric
          ? { nearFrom: 300, nearSays: 300, farFrom: 800, farSays: 800 }
          : { nearFrom: this.utilsService.yardToM(300), nearSays: 300, farFrom: this.utilsService.mileToM(0.5), farSays: 500 };
      case 3000:
        return metric
          ? { nearFrom: 1000, nearSays: 1000, farFrom: 3000, farSays: 3000 }
          : { nearFrom: this.utilsService.mileToM(1), nearSays: 1000, farFrom: this.utilsService.mileToM(2), farSays: 2000 };
      default:
        return null;
    }
  }

  private ReadCommandInWindow(currCommandDist: number, windows: CommandWindows): void {
    const windowLength = 35 * this.SpeedMul();
    if (currCommandDist > windows.nearFrom && currCommandDist < windows.nearFrom + windowLength && !this.command300mSaid) {
      this.SayTurnCommand(windows.nearSays);
      this.command300mSaid = true;
    }
    else if (currCommandDist > windows.farFrom && currCommandDist < windows.farFrom + windowLength && !this.command800mSaid) {
      this.SayTurnCommand(windows.farSays);
      this.command800mSaid = true;
    }
  }

  private checkChargerArrive(gpsData: GPSLocation): boolean {
    const nextChargerIdx = this.GetNextChargingStationIndexOnRoute();
    const dist = this.NextRoutePointDistance(gpsData, nextChargerIdx);
    if (nextChargerIdx != INVALID_TARGET && dist < 50 && this.arriveChargerSpeedThreshold()) {
      this.setChargeState(ChargeState.ArriveToCharger);
      const routePlanChargers: RoutePlanElement[] = this.NavigationRoute!.RoutePlan.filter((routePlanEl) => { return routePlanEl.Type == RoutePlanType.Charger });
      const nextChargingStationIndexFromChargers = this.GetNextChargingStationIndexFromChargers();
      this.ObservableArrivedToCharger.next(routePlanChargers[nextChargingStationIndexFromChargers])
      return true;
    }
    return false;
  }

  private arriveChargerSpeedThreshold(): boolean {
    return this.avg5Speed < 10;
  }

  private checkChargerExceed(): boolean {
    const nextChargerIdx = this.GetNextChargingStationIndexOnRoute();
    if (nextChargerIdx != INVALID_TARGET && nextChargerIdx < this.TargetIdx) {
      if (this.arriveChargerSpeedThreshold()) {
        this.setChargeState(ChargeState.ArriveToCharger);
      }
      this.TargetIdx = nextChargerIdx;
      return true;
    }
    return false;
  }

  private FindNextTargetWidthCommand(observeWayPoint: boolean): void {
    if (this.checkChargerExceed()) {
      return;
    }

    const LastTargetWithCommandIdx = this.TargetWithCommandIdx;
    this.TargetWithCommandIdx = this.TargetIdx;

    observeWayPoint = observeWayPoint && !!this.NextTurnInstruction && this.NextTurnInstruction.Id == "ARRIVE_TO_WAYPOINT";

    for (const turn of this.NavigationRoute!.Turns) {
      if (turn.RoutePointIdxStart >= this.TargetWithCommandIdx) {
        this.TargetWithCommandIdx = turn.RoutePointIdxStart;
        this.NextTurnInstruction = turn;
        break;
      }
    }

    if (LastTargetWithCommandIdx != INVALID_TARGET && LastTargetWithCommandIdx != this.TargetWithCommandIdx) {
      if (observeWayPoint) {
        this.NextWaypointIdx++;
        // NextWaypointIdx already owned by NavigationService
        this.ObservableNextWaypointIdx.next(this.NextWaypointIdx);
      }

      this.ObservableShowRoadLane.next(false);
    }
  }

  private IsWaitingForUserInteraction(): boolean {
    return this.ChargeState != ChargeState.None
  }

  private getMaxTargetIdx(): number {
    const nextWp = this.GetNextWaypointIndexOnRoute();
    const nextCh = this.GetNextChargingStationIndexOnRoute();
    return (nextCh == INVALID_TARGET) ? nextWp : Math.min(nextCh, nextWp);
  }

  /** Route point index currently targeted (still ahead of the driver); negative while invalid or replanning. */
  public getTargetIdx(): number {
    return this.TargetIdx;
  }

  public getChargeState(): ChargeState{
    return this.ChargeState;
  }

  public setChargeState(chargeState: ChargeState): void {
    this.ChargeState = chargeState;
    this.ObservableChargeStateChanged.next(this.ChargeState);
  }

  public RefreshTarget(gpsLocation: GPSLocation): void {
    // set actual location, calculating lastavgerage speed, distance from routeline
    this.CurrentPoint = gpsLocation;
    this.avg5Speed = this.getLastAverageSpeed(gpsLocation);
    const distFromRouteLine: number = this.RoadCurrSegmentAndPosMinDist(gpsLocation, true);

    if (this.IsWaitingForUserInteraction() || !this.navigation || !this.NavigationRoute?.RoutePoints) {
      return;
    }

    if (this.CurrentPoint.FirstRoutePoint) {
      return;
    }

    // replaning
    if (this.TargetIdx == REPLAN_TARGET_IDX) {
      this.ReplanIfOffRoute(gpsLocation, distFromRouteLine);
      return;
    }

    const currentDistFromRouteLine = this.UpdateTarget(gpsLocation, distFromRouteLine);
    if (currentDistFromRouteLine != null) {
      this.ReplanIfOffRoute(gpsLocation, currentDistFromRouteLine);
    }
  }

  /**
   * Moves the target along the route for a new position. Returns the distance from the
   * route line to check for an off-route replan, or null when the refresh has to stop
   * (arrived, at a charger, or already replanned).
   */
  private UpdateTarget(gpsLocation: GPSLocation, distFromRouteLine: number): number | null {
    // not valid targetidx
    if (this.TargetIdx == INVALID_TARGET) {
      return this.AcquireTarget(gpsLocation) ? distFromRouteLine : null;
    }
    // valid idx
    if (this.TargetIdx < this.NavigationRoute!.RoutePoints.length) {
      return this.FollowTarget(gpsLocation, distFromRouteLine);
    }
    return distFromRouteLine;
  }

  private IsOffRouteAndMoving(distFromRoute: number): boolean {
    return distFromRoute > REPLANNING_TOLERANCE && this.LastReplanningEarlier() && !this.IsStandingStill();
  }

  private ReplanIfOffRoute(gpsLocation: GPSLocation, distFromRouteLine: number): void {
    if (this.IsOffRouteAndMoving(distFromRouteLine)) {
      this.Replan(gpsLocation);
    }
  }

  private DistanceToRoutePoint(gpsLocation: GPSLocation, idx: number): number {
    const location = this.NavigationRoute!.RoutePoints[idx].Location;
    return this.utilsService.distanceBetweenCoordinates([gpsLocation.Latitude, gpsLocation.Longitude], [location.lat, location.lng]);
  }

  /** Finds a target from scratch. Returns false when it replanned instead. */
  private AcquireTarget(gpsLocation: GPSLocation): boolean {
    // find the nearest route point
    const maxTargetIdx = this.getMaxTargetIdx();
    const minDist = this.TargetNearestRoutePoint(gpsLocation, maxTargetIdx);

    if (this.NextChargingStationIdx != INVALID_TARGET && this.IsOffRouteAndMoving(minDist)) {
      this.Replan(gpsLocation);
      this.NextChargingStationIdx = INVALID_TARGET;
      return false;
    }

    this.NextChargingStationIdx = INVALID_TARGET;
    // havent reached the last coordinate of the route, and the deviation from the direction of travel greater than 140
    if (this.IsOffRouteAhead(gpsLocation)) {
      // Off the route ahead: nothing to target here, the off-route check at the end of RefreshTarget replans.
      return true;
    }
    this.LockOnTarget(gpsLocation, maxTargetIdx);
    return true;
  }

  /** Targets the route point nearest to the position (not past the next charging stop) and returns its distance. */
  private TargetNearestRoutePoint(gpsLocation: GPSLocation, maxTargetIdx: number): number {
    let minDist: number = NumberMaxValue;
    for (let i = 0; i < maxTargetIdx; i++) {
      if (this.NextChargingStationIdx != INVALID_TARGET && i > this.NextChargingStationIdx) {
        break;
      }

      const dist = this.DistanceToRoutePoint(gpsLocation, i);
      if (dist < minDist) {
        minDist = dist;
        this.TargetIdx = i;
      }
    }
    return minDist;
  }

  private IsOffRouteAhead(gpsLocation: GPSLocation): boolean {
    return !!this.NextTurnInstruction &&
      (this.TargetIdx >= this.NavigationRoute!.RoutePoints.length - 2 || this.DegreeDiff(gpsLocation, this.TargetIdx + 1) > 140);
  }

  private LockOnTarget(gpsLocation: GPSLocation, maxTargetIdx: number): void {
    if (this.TargetIdx == -1) {
      this.TargetIdx++;
    }

    // find the acutal targetidx
    if (this.DegreeDiff(gpsLocation, this.TargetIdx) > 90 && this.TargetIdx < this.NavigationRoute!.RoutePoints.length - 1 && this.DegreeDiff(gpsLocation, this.TargetIdx + 1) < 90) {
      this.TargetIdx++;
    }
    this.TargetIdx = Math.min(maxTargetIdx, this.TargetIdx);

    const roadAndPointMinDist: number = this.RoadAndPosMinDist(gpsLocation, true);

    this.TargetWithCommandIdx = this.TargetIdx;

    this.FindNextTargetWidthCommand(false);

    this.ClearSaidCommands();
    this.LastTargetWithCommandIdx = this.TargetWithCommandIdx;
    if (roadAndPointMinDist < 0.00015) {
      this.ReadNextCommand(this.NextTurnDistance(gpsLocation));
    }
    this.MinDist = NumberMaxValue;
  }

  /** Follows a valid target. Returns the distance from the route line, or null when the refresh has to stop. */
  private FollowTarget(gpsLocation: GPSLocation, distFromRouteLine: number): number | null {
    if (this.checkChargerArrive(gpsLocation)) {
      return null;
    }

    const currDist = this.DistanceToRoutePoint(gpsLocation, this.TargetIdx);
    if (this.MinDist > currDist) {
      this.MinDist = currDist;
    }

    if (this.UpdateTurnCommands(gpsLocation)) {
      return null;
    }

    let currentDistFromRouteLine = distFromRouteLine;
    if (currDist < this.Clamp(gpsLocation.Speed * 0.8, 55, 500) && currDist > this.MinDist + 3.5) {
      if (!this.AdvanceTarget(gpsLocation)) {
        return null;
      }
      currentDistFromRouteLine = this.RoadCurrSegmentAndPosMinDist(gpsLocation, true);
    }
    else if (currDist > this.MinDist + this.Clamp(gpsLocation.Speed * 0.8, 85, 500)) {
      this.SearchNearestPoint(gpsLocation);
    }

    this.EmitNextTurn(gpsLocation);
    return currentDistFromRouteLine;
  }

  /** Voice commands for the turn ahead. Returns true when the destination was reached. */
  private UpdateTurnCommands(gpsLocation: GPSLocation): boolean {
    if (this.LastTargetWithCommandIdx != this.TargetWithCommandIdx) {
      this.AnnounceNextTurnOncePassed(gpsLocation);
      return false;
    }

    // targetidx > routepoints length
    const currCommandDist = this.NextTurnDistance(gpsLocation);
    if (this.CheckArrive(currCommandDist)) {
      return true;
    }
    this.ReadCommands(currCommandDist);
    return false;
  }

  /** After passing a turn, announces the next one once clear of the previous turn. */
  private AnnounceNextTurnOncePassed(gpsLocation: GPSLocation): void {
    // remove show road lane parameter
    const points = this.NavigationRoute!.RoutePoints;
    const distanceCommands = this.utilsService.distanceBetweenCoordinates([points[this.TargetWithCommandIdx].Location.lat, points[this.TargetWithCommandIdx].Location.lng],
      [points[this.LastTargetWithCommandIdx].Location.lat, points[this.LastTargetWithCommandIdx].Location.lng]);

    let max: number = distanceCommands - Math.max(150, 50 * this.SpeedMul());
    max = this.Clamp(max, 12.5, 500);

    const distance = this.DistanceToRoutePoint(gpsLocation, this.LastTargetWithCommandIdx);
    const distanceNew = this.NextTurnDistance(gpsLocation);

    if (distance < this.LastCommandMinDist) {
      this.LastCommandMinDist = distance;
    }

    if (distance > this.Clamp(50 * this.SpeedMul(), 10, max) && distanceNew < this.LastCommandDistToShow && distance > this.LastCommandMinDist + 12.5) {
      this.ClearSaidCommands();
      this.ReadNextCommand(distanceNew);
      this.LastTargetWithCommandIdx = this.TargetWithCommandIdx;
    }

    this.LastCommandDistToShow = distanceNew;
  }

  /** Steps the target past the reached route point. Returns false when it ran off the end (arrived or back to a charger). */
  private AdvanceTarget(gpsLocation: GPSLocation): boolean {
    const points = this.NavigationRoute!.RoutePoints;
    const targerPos = [points[this.TargetIdx].Location.lat, points[this.TargetIdx].Location.lng];
    this.TargetIdx++;

    const maxTargetIdx = this.getMaxTargetIdx();

    while (this.TargetIdx < maxTargetIdx && this.utilsService.distanceBetweenCoordinates(targerPos, [points[this.TargetIdx].Location.lat, points[this.TargetIdx].Location.lng]) < gpsLocation.Speed / 3.6) {
      this.TargetIdx++;
    }

    if (this.TargetIdx >= points.length) {
      if (!this.checkChargerExceed()) {
        // Arrived
        this.HandleArrive();
      }
      return false;
    }

    this.MinDist = NumberMaxValue;
    this.FindNextTargetWidthCommand(true);

    if (this.LastTargetWithCommandIdx != this.TargetWithCommandIdx) {
      if (this.LastTargetWithCommandIdx == -1) this.LastTargetWithCommandIdx = this.TargetWithCommandIdx;
      this.LastCommandDistToShow = 0;
      this.LastCommandMinDist = NumberMaxValue;
    }
    return true;
  }

  /** Distance and time summed over the route points in [from, to). */
  private SumRouteLegs(from: number, to: number): { dist: number, time: number } {
    const points = this.NavigationRoute!.RoutePoints;
    let dist = 0;
    let time = 0;
    for (let i = from; i < to; i++) {
      dist += points[i].DistanceFromPreviousPoint;
      time += points[i].TimeFromPreviousPoint;
    }
    return { dist, time };
  }

  /** Distance and time from the position to the route point before `to`; nulls when there is no such stop. */
  private LegToRoutePoint(to: number | null, currDist: number, timeToNextTarget: number): { dist: number | null, time: number | null } {
    if (to == null) {
      return { dist: null, time: null };
    }
    const legs = this.SumRouteLegs(this.TargetIdx + 1, to);
    return { dist: legs.dist + currDist, time: legs.time + timeToNextTarget };
  }

  private IsBelowBatterySafetyLimit(soc: number): boolean {
    return soc * 100 < (this.vehicleService.getECarSettings()?.batterySafetyLimitRoute ?? 0);
  }

  private SyncPredictedBattery(predictedBattery: number | null): void {
    if (predictedBattery != null && !this.slidersValueChangedIn15seconds &&
      Math.round(this.vehicleService.getBattery()) != Math.round(predictedBattery)) {
      this.setBattery(false);
    }
  }

  private EmitNextTurn(gpsLocation: GPSLocation): void {
    const nextTurnDistance: number = this.NextTurnDistance(gpsLocation);
    if (!this.NextTurnInstruction || nextTurnDistance == NumberMaxValue || this.TargetIdx <= INVALID_TARGET) {
      return;
    }

    const points = this.NavigationRoute!.RoutePoints;
    const target = points[this.TargetIdx];
    // calculate distance & time to dest with last routepoints sum + dist to the next routepoint
    const toDest = this.SumRouteLegs(this.TargetIdx + 1, points.length);
    const currDist = this.DistanceToRoutePoint(gpsLocation, this.TargetIdx);
    const timeToNextTarget = (currDist / 1000) / (target.EVSpeedLimit == 0 ? target.RouteSpeedLimit : target.EVSpeedLimit) * 60;

    // calculate distance & time to next waypoint
    const nextWaypointRouteIdx = this.GetNextWaypointIndexOnRoute();
    const waypointAhead = nextWaypointRouteIdx != points.length - 1;
    const toWaypoint = this.LegToRoutePoint(waypointAhead ? nextWaypointRouteIdx + 1 : null, currDist, timeToNextTarget);
    const nextWaypointDisplayName = waypointAhead ? this.GetNextWaypointDisplayName() : "next waypoint";

    const nextChargingStationRouteIdx = this.GetNextChargingStationIndexOnRoute();
    const toChargingStation = this.LegToRoutePoint(nextChargingStationRouteIdx == INVALID_TARGET ? null : nextChargingStationRouteIdx, currDist, timeToNextTarget);

    const predictedBattery = this.getPredictedBattery();
    this.SyncPredictedBattery(predictedBattery);

    const routePoints = this.NavigationRoute!.RoutePoints;
    this.ObservableNextTurn.next({
      NextTurnInstruction: this.NextTurnInstruction, NextTurnDistance: nextTurnDistance, SpeedLimit: routePoints[this.TargetIdx].EVSpeedLimit,
      DistToDest: Math.round(toDest.dist + currDist), TimeToDest: Math.round(toDest.time + timeToNextTarget), EstimatedBatteryLevel: Math.round(predictedBattery ?? 0),
      DistToNextWaypoint: toWaypoint.dist, TimeToNextWaypoint: toWaypoint.time, NextWaypointDisplayName: nextWaypointDisplayName,
      DistToNextChargingStation: toChargingStation.dist, TimeToNextChargingStation: toChargingStation.time,
      NextWaypointBatteryReservedWarning: toWaypoint.dist != null && this.IsBelowBatterySafetyLimit(routePoints[nextWaypointRouteIdx].Soc),
      NextChargingStationBatteryReservedWarning: toChargingStation.dist != null && this.IsBelowBatterySafetyLimit(routePoints[nextChargingStationRouteIdx].Soc),
      DestBatteryReservedWarning: this.IsBelowBatterySafetyLimit(routePoints.at(-1)!.Soc)
    });
  }

  public SkipNextWaypoint(): void {
    this.NextWaypointIdx++;
    // NextWaypointIdx already owned by NavigationService
    this.ObservableNextWaypointIdx.next(this.NextWaypointIdx);
    this.Replan(this.CurrentPoint!, true);
    this.ObservableUserTriggeredReplan.next(true);
  }

  public SkipNextChargingStation(): void {
    if (this.IsNextChargingStationDestination()) {
      // Dropping the destination would leave nothing to navigate to.
      this.ObservableCannotSkipDestinationCharger.next();
      return;
    }

    let disabledStorage: any = localStorage.getItem("skippedChargingStation");
    if (disabledStorage && disabledStorage != 'null') {
      disabledStorage = JSON.parse(disabledStorage);
    } else {
      disabledStorage = [];
    }

    const nextChargingStationId = this.GetNextChargingStationIdOnRoute();
    if (nextChargingStationId != null && nextChargingStationId != "") {
      disabledStorage.push(nextChargingStationId.toString());
      localStorage.setItem("skippedChargingStation", JSON.stringify(disabledStorage));
      this.skipChargingStation = true;
    }

    // Disabling a station the user added as a waypoint is not enough: the
    // waypoint itself still routes the plan through it, so drop the stop.
    const chargingWaypointCsid = this.GetNextChargingStationWaypointCsid();
    if (chargingWaypointCsid) {
      this.waypointService.deleteChargingWaypoint(chargingWaypointCsid);
    }

    this.Replan(this.CurrentPoint!, true);
    this.ObservableUserTriggeredReplan.next(true);
  }

  private getLastAverageSpeed(geoPosition: GPSLocation): number {
    if (this.Last5Speed == null) {
      this.Last5Speed = [geoPosition.Speed, geoPosition.Speed, geoPosition.Speed, geoPosition.Speed, geoPosition.Speed];
    }
    else {
      this.Last5Speed.shift();
      this.Last5Speed.push(geoPosition.Speed)
    }
    let last5speedSum = 0;
    for (const speed of this.Last5Speed) {
      last5speedSum += speed;
    }
    return last5speedSum / this.Last5Speed.length;
  }

  private SearchNearestPointNoParam(): void {
    this.TargetIdx = INVALID_TARGET;
    this.TargetWithCommandIdx = INVALID_TARGET;
    this.LastTargetWithCommandIdx = INVALID_TARGET;
    this.ObservableShowRoadLane.next(false);
    if (this.CurrentPoint != null) {
      this.SearchNearestPoint(this.CurrentPoint);
    }
  }

  private SearchNearestPoint(geoPosition): void {
    this.NextChargingStationIdx = this.GetNextChargingStationIndexOnRoute();
    this.TargetIdx = INVALID_TARGET;
    this.TargetWithCommandIdx = INVALID_TARGET;
    this.LastTargetWithCommandIdx = INVALID_TARGET;
    this.ObservableShowRoadLane.next(false);
    this.RefreshTarget(geoPosition);
  }

  public StartNavigation(Points: Route): void {
    this.StopNavigation();
    if (this.ChargeState != ChargeState.None) {
      this.setChargeState(ChargeState.None);
    }
    this.NavigationRoute = Points;
    this.NextWaypointIdx = 0;
    this.ObservableNextWaypointIdx.next(null);
    // NextWaypointIdx already owned by NavigationService
    this.ClosingInOnTarget = false;
    this.ClearSaidCommands();

    this.RefreshTarget({
      Latitude: this.NavigationRoute.RoutePoints[0].Location.lat,
      Longitude: this.NavigationRoute.RoutePoints[0].Location.lng,
      Speed: 0,
      HorizontalAccuracy: 3,
      Course: this.cursorService.getBearing() ?? 0,
      FirstRoutePoint: true
    });
  }

  private StopNavigation(): void {
    this.TargetIdx = INVALID_TARGET;
    this.NextChargingStationIdx = INVALID_TARGET;
    this.TargetWithCommandIdx = INVALID_TARGET;
    this.LastTargetWithCommandIdx = INVALID_TARGET;
    this.ObservableShowRoadLane.next(false);
    this.NavigationRoute = null;
    this.NextTurnInstruction = null;
    this.ClosingInOnTarget = false;
  }

  private NextTurnDistance(gpsData: GPSLocation): number {
    return this.NextRoutePointDistance(gpsData, this.TargetWithCommandIdx);
  }

  private NextRoutePointDistance(gpsData: GPSLocation, index: number): number {
    if (this.NavigationRoute?.RoutePoints && this.TargetIdx < this.NavigationRoute.RoutePoints.length && this.TargetIdx > INVALID_TARGET && this.NavigationRoute.RoutePoints.length > index) {
      let distance = this.utilsService.distanceBetweenCoordinates([gpsData.Latitude, gpsData.Longitude],
        [this.NavigationRoute.RoutePoints[this.TargetIdx].Location.lat, this.NavigationRoute.RoutePoints[this.TargetIdx].Location.lng]);
      for (let i = this.TargetIdx; i < index; i++) {
        distance += this.utilsService.distanceBetweenCoordinates([this.NavigationRoute.RoutePoints[i].Location.lat, this.NavigationRoute.RoutePoints[i].Location.lng],
          [this.NavigationRoute.RoutePoints[i + 1].Location.lat, this.NavigationRoute.RoutePoints[i + 1].Location.lng]);
      }
      return distance;
    }
    return NumberMaxValue;
  }

  private SpeedMul(): number {
    return this.Clamp(this.avg5Speed / 35, 1, 5);
  }

  private Replan(loc: GPSLocation, backgroundReplan: boolean = false, onGoingReplan: boolean = false): void {
    if (!this.ReplanningNow) {
      this.waypointService.triggerParamsUpdate();

      if (this.NavigationRoute?.RoutePoints &&
        this.NavigationRoute.RoutePoints.length > 0 && !this.slidersValueChangedIn15seconds && !onGoingReplan) {
        this.setBattery(false);
      }
      this.ReplanningNow = true;
      this.audioService.mergeAndPlayAudio([this.audioService.getAudioSource() + "/Commands/Replan.mp3"]);

      if (!backgroundReplan) {
        this.TargetIdx = REPLAN_TARGET_IDX;
      }

      this.LastReplanTime = Date.now();
      this.evCalculationService.requestReplan({ forceRecalc: !this.slidersValueChangedIn15seconds });
      // Notification only — AnalyticsLoggerService subscribes to "replan" on
      // ObservableChangeNavigation to flag the next telemetry batch.
      // The actual replan work is now done by EvCalculationService above;
      // this emit no longer drives any subscriber's behaviour.
      this.ObservableChangeNavigation.next("replan");
    }
    this.ReplanningNow = false;
  }

  private GetBatteryBeforeAndAfterNextCharge(): [number, number] {
    const nextChargingStationIdx = this.GetNextChargingStationIndexOnRoute();
    if (this.NavigationRoute?.RoutePoints) {
      if (nextChargingStationIdx != -1 && this.NavigationRoute.RoutePoints.length > nextChargingStationIdx + 1) {
        return [Math.max(this.NavigationRoute.RoutePoints[nextChargingStationIdx].Soc, 1),
        this.NavigationRoute.RoutePoints[nextChargingStationIdx + 1].Soc]
      }
    }
    return [this.vehicleService.getBattery() ? this.vehicleService.getBattery() : 1, 100]
  }

  public getPredictedBattery(): number | null {
    if (this.NavigationRoute?.RoutePoints) {
      if (this.TargetIdx >= this.NavigationRoute.RoutePoints.length) {
        return this.NavigationRoute.RoutePoints.at(-1)!.Soc * 100;
      }
      if (this.TargetIdx <= 0) {
        return this.NavigationRoute.RoutePoints[0].Soc * 100;
      }
      // interpolate battery level between route points
      const routePointsDist: number = this.utilsService.distanceBetweenCoordinates([this.NavigationRoute.RoutePoints[this.TargetIdx - 1].Location.lat, this.NavigationRoute.RoutePoints[this.TargetIdx - 1].Location.lng], [this.NavigationRoute.RoutePoints[this.TargetIdx].Location.lat, this.NavigationRoute.RoutePoints[this.TargetIdx].Location.lng]);
      const locationToTargetDist: number = this.utilsService.distanceBetweenCoordinates([this.CurrentPoint!.Latitude, this.CurrentPoint!.Longitude], [this.NavigationRoute.RoutePoints[this.TargetIdx].Location.lat, this.NavigationRoute.RoutePoints[this.TargetIdx].Location.lng]);
      let alpha: number = locationToTargetDist / routePointsDist;
      if (alpha > 1) { alpha = 1 }

      return this.utilsService.Lerpd(this.NavigationRoute.RoutePoints[this.TargetIdx - 1].Soc * 100, this.NavigationRoute.RoutePoints[this.TargetIdx].Soc * 100, 1 - alpha);
    }
    else {
      return null;
    }
  }

  private getBattery(): number {
    const predictedBattery = this.getPredictedBattery();
    if (predictedBattery != null && Math.abs(this.vehicleService.getBattery() - predictedBattery) < 10) {
      return predictedBattery;
    }
    else {
      return -1;
    }
  }

  private setBattery(exitNavigation: boolean = false) {
    const predictedBattery = this.getPredictedBattery();
    if (exitNavigation) {
      this.vehicleService.ObservableBatteryChanged.next(this.vehicleService.getBattery());
    }
    else if (predictedBattery != null && predictedBattery < this.vehicleService.getBattery()) {
      // Silent: the predicted SOC is derived from the active route, so feeding it
      // back through setBattery() would emit ObservableECarSettings and kick off a
      // replan of the very route it came from.
      this.vehicleService.setBatterySilent(predictedBattery);
      this.vehicleService.ObservableBatteryChanged.next(predictedBattery);
    }
  }

  // While stationary (e.g. waiting indoors) GPS jitter can push the reported
  // position off the route line; an off-route replan is only meaningful when
  // actually moving, so keep the current route until there is real movement.
  private IsStandingStill(): boolean {
    return this.avg5Speed < 5;
  }

  private LastReplanningEarlier(): boolean {
    const dif = Date.now() - new Date(this.LastReplanTime).getTime();
    const Seconds_from_T1_to_T2 = dif / 1000;
    const Seconds_Between_Dates = Math.abs(Seconds_from_T1_to_T2);

    return Seconds_Between_Dates > 5;
  }

  private RoadCurrSegmentAndPosMinDist(geoPosition: GPSLocation, onlyBeforeWaypoint: boolean): number {
    if (this.TargetWithCommandIdx != -1 && this.TargetWithCommandIdx > 0) {
      let minDist = NumberMaxValue;
      const nextChargingStationIdx = this.GetNextChargingStationIndexOnRoute();
      const nextWaypointIdx = this.GetNextWaypointIndexOnRoute() - 1;
      // NextWaypointIdx already owned by NavigationService
      const maxPtIndex = nextChargingStationIdx == INVALID_TARGET ? nextWaypointIdx : Math.min(nextWaypointIdx, nextChargingStationIdx - 1);

      for (let i = maxPtIndex; i >= 0; i--) {
        const dist = this.LineDist(this.NavigationRoute!.RoutePoints[i].Location.lng * Math.cos(this.utilsService.toRad(this.NavigationRoute!.RoutePoints[i].Location.lat)),
          this.NavigationRoute!.RoutePoints[i].Location.lat,
          this.NavigationRoute!.RoutePoints[i + 1].Location.lng * Math.cos(this.utilsService.toRad(this.NavigationRoute!.RoutePoints[i + 1].Location.lat)),
          this.NavigationRoute!.RoutePoints[i + 1].Location.lat,
          geoPosition.Longitude * Math.cos(this.utilsService.toRad(geoPosition.Latitude)),
          geoPosition.Latitude);
        if (dist < minDist) {
          minDist = dist;
        }
      }
      return minDist;
    }
    else {
      return this.RoadAndPosMinDist(geoPosition, onlyBeforeWaypoint);
    }
  }

  private RoadAndPosMinDist(geoPosition: GPSLocation, onlyBeforeWaypoint: boolean): number {
    let minDist = NumberMaxValue;
    let maxPtIndex = onlyBeforeWaypoint ? (this.GetNextWaypointIndexOnRoute() - 1) : (this.NavigationRoute!.RoutePoints.length - 1);
    for (let i = 0; i < maxPtIndex; i++) {
      const dist = this.LineDist(this.NavigationRoute!.RoutePoints[i].Location.lng * Math.cos(this.utilsService.toRad(this.NavigationRoute!.RoutePoints[i].Location.lat)),
        this.NavigationRoute!.RoutePoints[i].Location.lat,
        this.NavigationRoute!.RoutePoints[i + 1].Location.lng * Math.cos(this.utilsService.toRad(this.NavigationRoute!.RoutePoints[i + 1].Location.lat)),
        this.NavigationRoute!.RoutePoints[i + 1].Location.lat,
        geoPosition.Longitude * Math.cos(this.utilsService.toRad(geoPosition.Latitude)),
        geoPosition.Latitude);
      if (dist < minDist) {
        minDist = dist;
      }
    }
    return minDist;
  }

  public RoadAndPosMinDistCoords(geoPosition: GPSLocation): number[] {
    let minDist = NumberMaxValue;
    let segment = 0;
    for (let i = 0; i < this.NavigationRoute!.RoutePoints.length - 2; i++) {
      const dist = this.LineDist(this.NavigationRoute!.RoutePoints[i].Location.lng * Math.cos(this.utilsService.toRad(this.NavigationRoute!.RoutePoints[i].Location.lat)),
        this.NavigationRoute!.RoutePoints[i].Location.lat,
        this.NavigationRoute!.RoutePoints[i + 1].Location.lng * Math.cos(this.utilsService.toRad(this.NavigationRoute!.RoutePoints[i + 1].Location.lat)),
        this.NavigationRoute!.RoutePoints[i + 1].Location.lat,
        geoPosition.Longitude * Math.cos(this.utilsService.toRad(geoPosition.Latitude)),
        geoPosition.Latitude);
      if (dist < minDist) {
        minDist = dist;
        segment = i;
      }
    }
    return [this.NavigationRoute!.RoutePoints[segment].Location.lat, this.NavigationRoute!.RoutePoints[segment].Location.lng];
  }

  public GetClosestPoint(geoPosition: [number, number]): [number[], number] {
    if (this.TargetIdx > 0 && this.TargetIdx < this.NavigationRoute!.RoutePoints.length - 2) {
      let iMin = 0;
      let iMax = 1;
      if (this.TargetIdx > 1) {
        iMin = -1;
      }

      let minDist = NumberMaxValue;
      let resp: [number[], number] | null = null;
      for (let i = iMin; i <= iMax; i++) {
        const lastTarget = [this.NavigationRoute!.RoutePoints[this.TargetIdx + i - 1].Location.lat, this.NavigationRoute!.RoutePoints[this.TargetIdx + i - 1].Location.lng];
        const actTarget = [this.NavigationRoute!.RoutePoints[this.TargetIdx + i].Location.lat, this.NavigationRoute!.RoutePoints[this.TargetIdx + i].Location.lng];
        const distAndPoint: any = this.LineDistAndPoint(lastTarget[0], lastTarget[1], actTarget[0], actTarget[1], geoPosition[0], geoPosition[1]);
        if (distAndPoint[0] < minDist) {
          minDist = distAndPoint[0];
          resp = [distAndPoint[1], i];
        }
      }
      if (resp && this.utilsService.distanceBetweenCoordinates(geoPosition, resp[0]) <= 50) {
        return resp;
      }
    }

    return [geoPosition, -2];
  }

  private GetNextWaypointIndexOnRoute(): number {
    let passedWaypointsCount = this.NextWaypointIdx;

    if (this.NavigationRoute?.Turns) {
      for (let i = 0; i < this.NavigationRoute.Turns.length - 1; i++) {
        if (this.NavigationRoute.Turns[i].Id == "ARRIVE_TO_WAYPOINT") {
          if ((--passedWaypointsCount) < 0) {
            return this.NavigationRoute.Turns[i].RoutePointIdxStart;
          }
        }
      }
    }

    if (this.NavigationRoute) {
      return this.NavigationRoute.RoutePoints.length - 1;
    } else {
      return 0;
    }
  }

  private GetNextWaypointDisplayName(): string {
    const routePlanWayPoints: RoutePlanElement[] = this.NavigationRoute!.RoutePlan.filter((routePlanEl) => { return routePlanEl.Type == RoutePlanType.WayPoint });
    if (routePlanWayPoints.length > this.NextWaypointIdx) {
      return routePlanWayPoints[this.NextWaypointIdx].Name;
    }
    else {
      return "";
    }
  }

  private GetNextChargingStationIndexOnRoute(): number {
    if (this.NavigationRoute) {
      for (const turn of this.NavigationRoute.Turns) {
        if (turn.RoutePointIdxStart >= this.TargetWithCommandIdx) {
          if (this.utilsService.isChargeId(turn.Id)) {
            return turn.RoutePointIdxStart;
          }
        }
      }
    }
    return INVALID_TARGET;
  }

  private GetNextChargingStationIndexFromChargers(): number {
    let nextChargerIdx = -1;

    if (this.NavigationRoute) {
      for (const turn of this.NavigationRoute.Turns) {
        if (this.utilsService.isChargeId(turn.Id)) {
          nextChargerIdx++;
        }
        if (turn.RoutePointIdxStart >= this.TargetWithCommandIdx) {
          if (this.utilsService.isChargeId(turn.Id)) {
            break;
          }
        }
      }
    }

    return nextChargerIdx;
  }

  private GetNextChargingStationIdOnRoute(): string | null {
    return this.GetNextChargingStationOnRoute()?.ChargingStationId ?? "";
  }

  private GetNextChargingStationOnRoute(): RoutePlanElement | null {
    // A charger that is also the destination is parsed as ChargerEndPoint, but
    // its turn is still a charge turn, so it has to be counted here as well.
    const routePlanWayChargers: RoutePlanElement[] = this.NavigationRoute!.RoutePlan.filter((routePlanEl) => {
      return routePlanEl.Type == RoutePlanType.Charger || routePlanEl.Type == RoutePlanType.ChargerEndPoint;
    });
    const nextChargerIdx = this.GetNextChargingStationIndexFromChargers();

    if (nextChargerIdx >= 0 && routePlanWayChargers.length > nextChargerIdx) {
      return routePlanWayChargers[nextChargerIdx];
    }
    else {
      return null;
    }
  }

  /** The csid of the next charging stop when the user added it as a waypoint (as opposed to one the planner picked). */
  private GetNextChargingStationWaypointCsid(): string | null {
    if (!this.NavigationRoute) {
      return null;
    }
    const nextChargingStationId = this.GetNextChargingStationIdOnRoute();
    if (!nextChargingStationId) {
      return null;
    }

    const waypoints = this.waypointService.getWaypointsParams();
    // Index 0 is the start, re-anchored to the current position on every replan.
    const waypoint = waypoints.find((wp, i) => i > 0 && wp.csid != null && String(wp.csid) == String(nextChargingStationId));
    return waypoint?.csid ?? null;
  }

  public IsNextChargingStationDestination(): boolean {
    if (!this.NavigationRoute) {
      return false;
    }
    return this.GetNextChargingStationOnRoute()?.Type == RoutePlanType.ChargerEndPoint;
  }

  //calculate navigation angle
  public GetNavigationCursorAngle(index: number): number {
    let lastTargetIdx = this.TargetIdx + index - 1;
    let actTargetIdx = this.TargetIdx + index;
    if (lastTargetIdx <= -1) {
      lastTargetIdx = 0;
      actTargetIdx = 1;
    }
    const lastTarget = [this.NavigationRoute!.RoutePoints[lastTargetIdx].Location.lat, this.NavigationRoute!.RoutePoints[lastTargetIdx].Location.lng];
    const actTarget = [this.NavigationRoute!.RoutePoints[actTargetIdx].Location.lat, this.NavigationRoute!.RoutePoints[actTargetIdx].Location.lng];
    return this.utilsService.angleFromCoordinate(lastTarget[0], lastTarget[1], actTarget[0], actTarget[1]);
  }


  private Clamp(x: number, min: number, max: number): number {
    if (x < min) {
      return min;
    }
    if (x < max) {
      return x;
    }
    return max; // also for NaN, unlike Math.min
  }

  private Bearing(lat_1: number, lon_1: number, lat_2: number, lon_2: number): number {
    let degree = Math.atan2(-1 * Math.sin(this.utilsService.toRad(lon_1 - lon_2)) * Math.cos(this.utilsService.toRad(lat_2)),
      Math.cos(this.utilsService.toRad(lat_1)) * Math.sin(this.utilsService.toRad(lat_2)) - Math.sin(this.utilsService.toRad(lat_1)) * Math.cos(this.utilsService.toRad(lat_2)) * Math.cos(this.utilsService.toRad(lon_1 - lon_2))) * 180 / Math.PI;
    degree = this.NormDegree(degree);
    return degree;
  }

  private NormDegree(degree: number): number {
    if (degree < 0) {
      return degree + 360;
    }
    else if (degree >= 360) {
      return degree - 360;
    }
    else {
      return degree;
    }
  }

  private NormAngle(angle: number): number {
    if (angle > 180) {
      angle -= 360;
      angle = Math.abs(angle);
    }
    return angle;
  }

  private LineDist(x1: number, y1: number, x2: number, y2: number, x: number, y: number): number {
    const A = x - x1;
    const B = y - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const len_sq = C * C + D * D;
    const param = dot / len_sq;

    let xx, yy;

    if (param < 0) {
      xx = x1;
      yy = y1;
    }
    else if (param > 1) {
      xx = x2;
      yy = y2;
    }
    else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }

    return this.dist(x, y, xx, yy);
  }

  private LineDistAndPoint(x1: number, y1: number, x2: number, y2: number, x: number, y: number): [number, number[]] {
    const A = x - x1;
    const B = y - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const len_sq = C * C + D * D;
    const param = dot / len_sq;

    let xx, yy;

    if (param < 0) {
      xx = x1;
      yy = y1;
    }
    else if (param > 1) {
      xx = x2;
      yy = y2;
    }
    else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }

    return [this.dist(x, y, xx, yy), [xx, yy]];
  }

  private distance(lat_1: number, lon_1: number, lat_2: number, lon_2: number, alt1: number, alt2: number): number {
    let distance = 0;
    let d_lat = 0;
    let d_lon = 0;
    let a = 0;
    let c = 0;
    d_lat = lat_2 - lat_1;
    d_lon = lon_2 - lon_1;
    a = Math.sin(this.utilsService.toRad(d_lat / 2)) * Math.sin(this.utilsService.toRad(d_lat / 2)) + Math.cos(this.utilsService.toRad(lat_1)) * Math.cos(this.utilsService.toRad(lat_2)) * Math.sin(this.utilsService.toRad(d_lon / 2)) * Math.sin(this.utilsService.toRad(d_lon / 2));
    c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    distance = c * 6366707;
    return distance;
  }

  private dist(x1, y1, x2, y2) {
    return Math.sqrt(((x1 - x2) * (x1 - x2)) + ((y1 - y2) * (y1 - y2)));
  }

  /*private GetCommand(rp) {
    if (rp.Id == "ENTER_ROUNDABOUT" ||
      rp.Id == "EXIT_ROUNDABOUT") {
      if (rp.ManeuverData) {
        if (rp.Id == "EXIT_ROUNDABOUT") {
          files.Add(string.Format(Utils.GetVoiceFile("Commands/Take1Exit.wav"), rp.ManeuverData));
          this.AddToAudioPlayList(this.voiceFolder + "Commands/Take1Exit.mp3");
        }
        else {   // EnterRoundabout
          if (Settings.Instance.VoiceDirectory.StartsWith("es-ES")) {
            files.Add(string.Format(Utils.GetVoiceFile("Commands/AtRoundaboutTake{0}Exit.wav"), rp.ManeuverData));
          }
          else {
            files.Add(Utils.GetVoiceFile("Commands/AtRoundabout.wav"));
            files.Add(string.Format(Utils.GetVoiceFile("Commands/Take{0}Exit.wav"), rp.ManeuverData));
          }
        }
      }
      else {
        files.Add(Utils.GetVoiceFile(CommandFiles[(int)rp.Maneuver]));
      }
    }
    else {
      files.Add(Utils.GetVoiceFile(CommandFiles[(int)rp.Maneuver]));
    }
    return files;
  }*/

  private loadGpxAsRoute(xmlRoot: Document): ReplayGpsPoint[] {
    let lastBearing = 0;
    const pointarray: ReplayGpsPoint[] = [];
    let lastPoint: number[] | null = null;
    for (const trackpoint of this.getGpxTrackpoints(xmlRoot)) {
      const point = this.parseGpxTrackpoint(trackpoint, lastBearing);
      if (lastPoint != null && ((lastPoint[0] != point.mPosition[0]) && (lastPoint[1] != point.mPosition[1]))) {
        point.mBearing = this.utilsService.angleFromCoordinate(lastPoint[0], lastPoint[1], point.mPosition[0], point.mPosition[1]);
        lastBearing = point.mBearing;
      }
      else {
        point.mBearing = lastBearing;
      }
      lastPoint = point.mPosition;
      pointarray.push(point);
    }
    return pointarray;
  }

  /** All trkpt elements of the gpx, in document order (trk > trkseg > trkpt). */
  private getGpxTrackpoints(xmlRoot: Document): Element[] {
    const trackpointElements: Element[] = [];
    const tracks = xmlRoot.documentElement.getElementsByTagName("trk");
    for (const track of tracks) {
      const segments = track.getElementsByTagName("trkseg");
      for (const segment of segments) {
        const trackpoints = segment.getElementsByTagName("trkpt");
        for (const trackpoint of trackpoints) {
          trackpointElements.push(trackpoint);
        }
      }
    }
    return trackpointElements;
  }

  /** Parses position, time, elevation and speed of a trkpt; the bearing is left for the caller. */
  private parseGpxTrackpoint(trackpoint: Element, bearing: number): ReplayGpsPoint {
    const point: ReplayGpsPoint = {
      mPosition: [
        Number.parseFloat(trackpoint.getAttribute("lat")!),
        Number.parseFloat(trackpoint.getAttribute("lon")!)
      ],
      mDateTime: null,
      mHeight: null,
      mBearing: bearing,
      mSpeed: null
    };
    const times = trackpoint.getElementsByTagName("time");
    if (times.length > 0) {
      point.mDateTime = new Date(times[0].innerHTML);
    }
    const elevations = trackpoint.getElementsByTagName("ele");
    if (elevations.length > 0) {
      point.mHeight = Number.parseFloat(elevations[0].innerHTML);
    }
    const extensions = trackpoint.getElementsByTagName("extensions");
    if (extensions.length > 0) {
      const gpss = extensions[0].getElementsByTagName("gte:gps");
      if (gpss.length > 0) {
        point.mSpeed = Number.parseFloat(gpss[0].getAttribute("speed")!);
      }
    }
    return point;
  }

  // adding random points between two planpoints for simulating a real life gps situation
  public generateFakeGpsData(routePoints: RoutePoint[]): { mPosition: number[], mDateTime: Date | null, mHeight: number | null, mBearing: number, mSpeed: number | null }[] {
    // if a gpx is selected, replay that, instead of the planned route
    if (this.selectedReplayTrack != null) {
      return this.loadGpxAsRoute(this.selectedReplayTrack);
    }

    const pointsWithTimeArray: ReplayGpsPoint[] = [];
    const DateTime = new Date();
    let lastBearing = this.utilsService.angleFromCoordinate(routePoints[0].Location.lat, routePoints[0].Location.lng,
      routePoints[1].Location.lat, routePoints[1].Location.lng);

    for (let i = 1; i < routePoints.length; i++) {
      lastBearing = this.appendFakeGpsSegment(pointsWithTimeArray, routePoints[i - 1], routePoints[i], DateTime, lastBearing);
    }

    pointsWithTimeArray.push({
      mPosition: [routePoints.at(-1)!.Location.lat, routePoints.at(-1)!.Location.lng],
      mDateTime: new Date(), mHeight: 0, mBearing: 0, mSpeed: this.avg5Speed
    })

    return pointsWithTimeArray;
  }

  /** Appends the fake gps points between two route points (advancing dateTime), returns the updated last bearing. */
  private appendFakeGpsSegment(pointsWithTimeArray: ReplayGpsPoint[], from: RoutePoint, to: RoutePoint, dateTime: Date, lastBearing: number): number {
    const VehicleSpeedInMeterPerSecond = this.utilsService.KmphToMps(to.RouteSpeedLimit);
    const DistanceInMeter = this.utilsService.distanceBetweenCoordinates([from.Location.lat, from.Location.lng],
      [to.Location.lat, to.Location.lng]);
    let DistanceInSecond = 0;
    const randomNoise = ((Math.PI * 2) / DGTEARTHCIRCUMFERENCE_M) * 3;

    if (VehicleSpeedInMeterPerSecond > 0) {
      DistanceInSecond = DistanceInMeter / VehicleSpeedInMeterPerSecond;
    }

    let Grade = 0;
    if (DistanceInMeter != 0) {
      Grade = 1 / DistanceInMeter;
    }

    if (DistanceInSecond < 1) {
      return this.appendFakeGpsSinglePoint(pointsWithTimeArray, from, to, dateTime, lastBearing, DistanceInSecond, randomNoise);
    }
    return this.appendFakeGpsInterpolatedPoints(pointsWithTimeArray, from, to, dateTime, lastBearing,
      { speed: VehicleSpeedInMeterPerSecond, durationInSecond: DistanceInSecond, grade: Grade, randomNoise: randomNoise });
  }

  /** Short segment: a single noisy point at the segment start. */
  private appendFakeGpsSinglePoint(pointsWithTimeArray: ReplayGpsPoint[], from: RoutePoint, to: RoutePoint, dateTime: Date,
    lastBearing: number, DistanceInSecond: number, randomNoise: number): number {
    const PointWithTime: ReplayGpsPoint = {
      mPosition: [], mDateTime: null, mHeight: null, mBearing: lastBearing, mSpeed: null
    };

    PointWithTime.mPosition = [this.utilsService.toRad(from.Location.lat), this.utilsService.toRad(from.Location.lng)];

    const lonRand = randomNoise * Math.cos(PointWithTime.mPosition[0]);

    PointWithTime.mPosition[1] += this.utilsService.getRandomBetweenCoordinates(-lonRand, lonRand);
    PointWithTime.mPosition[0] += this.utilsService.getRandomBetweenCoordinates(-randomNoise, randomNoise);
    dateTime.setSeconds(dateTime.getSeconds() + DistanceInSecond);
    PointWithTime.mDateTime = new Date(dateTime);
    PointWithTime.mHeight = 100;
    PointWithTime.mSpeed = this.avg5Speed;
    PointWithTime.mBearing = this.getFakeGpsSegmentBearing(from, to, lastBearing);
    PointWithTime.mPosition[0] = this.utilsService.toDeg(PointWithTime.mPosition[0]);
    PointWithTime.mPosition[1] = this.utilsService.toDeg(PointWithTime.mPosition[1]);
    pointsWithTimeArray.push(PointWithTime);
    return PointWithTime.mBearing;
  }

  /** Long segment: one noisy point per second along the great circle from the segment start. */
  private appendFakeGpsInterpolatedPoints(pointsWithTimeArray: ReplayGpsPoint[], from: RoutePoint, to: RoutePoint, dateTime: Date,
    lastBearing: number, segment: { speed: number, durationInSecond: number, grade: number, randomNoise: number }): number {
    const PosFrom = [this.utilsService.toRad(from.Location.lat), this.utilsService.toRad(from.Location.lng)];

    const brng = this.utilsService.toRad(this.utilsService.angleFromCoordinate(from.Location.lat, from.Location.lng,
      to.Location.lat, to.Location.lng));
    for (let t = 0; t < segment.durationInSecond; t += 1) {
      const PointWithTime: ReplayGpsPoint = {
        mPosition: [], mDateTime: null, mHeight: null, mBearing: lastBearing, mSpeed: null
      };
      const d = segment.speed * t;
      const dR = d / DGTEARTHRADIUS_M;
      const lon1 = PosFrom[1];
      const lat1 = PosFrom[0];

      const cosLat1 = Math.cos(lat1);
      const sinLat1 = Math.sin(lat1);
      const cosDR = Math.cos(dR);
      const sinDR = Math.sin(dR);

      const lonRand = segment.randomNoise * cosLat1;

      const lat2 = Math.asin(sinLat1 * cosDR + cosLat1 * sinDR * Math.cos(brng)) + this.utilsService.getRandomBetweenCoordinates(-segment.randomNoise, segment.randomNoise);
      const lon2 = lon1 + Math.atan2(Math.sin(brng) * sinDR * cosLat1, cosDR - sinLat1 * Math.sin(lat2)) + this.utilsService.getRandomBetweenCoordinates(-lonRand, lonRand);
      const Height = 100 + segment.grade * d;
      PointWithTime.mPosition = [lat2, lon2];
      PointWithTime.mDateTime = new Date(dateTime);
      PointWithTime.mHeight = Height;
      dateTime.setSeconds(dateTime.getSeconds() + 1);
      PointWithTime.mSpeed = to.RouteSpeedLimit;
      PointWithTime.mBearing = this.getFakeGpsSegmentBearing(from, to, lastBearing);
      lastBearing = PointWithTime.mBearing;
      PointWithTime.mPosition[0] = this.utilsService.toDeg(PointWithTime.mPosition[0]);
      PointWithTime.mPosition[1] = this.utilsService.toDeg(PointWithTime.mPosition[1]);
      pointsWithTimeArray.push(PointWithTime);
    }
    return lastBearing;
  }

  /** Bearing of the segment, or the previous bearing when the segment is axis-aligned or degenerate. */
  private getFakeGpsSegmentBearing(from: RoutePoint, to: RoutePoint, lastBearing: number): number {
    if ((from.Location.lat != to.Location.lat) && (from.Location.lng != to.Location.lng)) {
      return this.utilsService.angleFromCoordinate(from.Location.lat, from.Location.lng, to.Location.lat, to.Location.lng);
    }
    return lastBearing;
  }
}
